import { Endpoint } from '@business/api/endpoint';
import { HTMLService } from '@business/services';
import { FormControl, useFormField } from '@core/utils/form';
import { R } from '@core/utils/r';
// import { Jodit } from 'jodit-pro';
import JoditEditor, { Jodit } from 'jodit-pro-react';
import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { InputProps } from 'reactstrap';
import { Observable } from 'rxjs';
import { FormInputProps } from './FormInput';
import './form_html.scss';
import { GlossaryService } from '@business/services/glossary_service';
import { useLazyEffect } from '@core/utils/react';
import { useShortCodes } from './short_codes_context';
import { attachShortcodeHighlight } from './shortcode_highlight';
import { attachBlackForecolor } from './black_forecolor';

export function FormHTMLInline<C extends FormControl<any>>(
  props: FormInputProps<C> &
    InputProps & {
      theme?: string;
      readonly?: boolean;
      minHeight?: number;
      media?: (text?: string) => Promise<Observable<string>>;
      autoFocus?: boolean;
      onFocus?: () => void;
      onBlur?: () => void;
      transformedFieldsValue?: Record<string, string>;
      onText?(text: string): void;
      glossarify?: boolean;
      plainText?: boolean;
      list?: any;
    },
) {
  const { autoFocus = false, onText, glossarify = false } = props;
  const { formControl, fieldState, fieldConfig } = useFormField(props);

  // Extra short codes contributed by the surrounding builder (e.g. project-list templates).
  // Appended after the default list so they show only where a provider supplies them.
  const extraShortCodes = useShortCodes();

  const fieldValue = R.toString(fieldState.value) || '';
  const [isFocused, setIsFocused] = React.useState(false);
  const [myValue, setValue] = React.useState(fieldValue);

  useEffect(() => {
    if (myValue !== fieldValue && !isFocused) {
      setValue(fieldValue);
    }
  }, [myValue, fieldValue, isFocused]);

  const [joditKey, setJoditKey] = React.useState(R.uniqueId('jodit_'));

  const fieldReadonly = fieldConfig.readonly || false;

  const fieldPlaceholder = fieldConfig.placeholder || '';
  useEffect(() => {
    // setValue(fieldValue);
    if (!isFocused) {
      setJoditKey(R.uniqueId('jodit_'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue]);

  // Contributed short codes (e.g. project-list templates) resolve async — after this editor's
  // first init. Re-create the editor when they arrive so the "Short Codes" dropdown rebuilds with them.
  useEffect(() => {
    if (extraShortCodes && !isFocused) {
      setJoditKey(R.uniqueId('jodit_'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraShortCodes]);

  const handleChangeText = useCallback(
    (value: string) => {
      if (isFocused) {
        formControl.setValue(value);
        onText && onText(value);
      }
    },
    [formControl, isFocused, onText],
  );

  const handleBlur = useCallback(
    (txtValue: string) => {
      formControl.onBlur();
      props.onBlur && props.onBlur();
      formControl.setValue(txtValue);
      setTimeout(() => {
        setIsFocused(false);
      }, 1000);
    },
    [formControl, props],
  );

  const handleFocus = useCallback(() => {
    formControl.onFocus();
    props.onFocus && props.onFocus();
    setIsFocused(true);
  }, [formControl, props]);

  const joditRef = useRef<Jodit | null>(null);

  const config: Partial<Jodit['options']> = useMemo(() => {
    const headers = Endpoint.dumpHeaders;
    delete headers['Content-Type'];
    delete headers['Accept'];
    // The editor this config belongs to (set in afterInit): Jodit doesn't pass it to event handlers like afterCommand
    let editorInstance: Jodit | null = null;

    return {
      // ...Jodit.defaultOptions,
      license: 'M1NDC-1DHE1-C05P6-DPFOA',
      disablePlugins: [
        'button-generator',
        'google-maps',
        'google-search',
        'page-break',
        'finder',
        'backup',
        'emoji',
        'keyboard',
        'export-docs',
        'change-case',
        'show-blocks',
        'paste-code',
      ],
      // IMPORTANT: keep this schema EMPTY. The jodit-pro "highlight-signature" plugin only hooks the
      // editor's `change` event (re-walking + re-wrapping the DOM on every keystroke) when the schema
      // has entries. A non-empty schema (previously highlighting `{{tokens}}` yellow) rewrote the HTML
      // on every input, which reset the caret and made a single backspace delete several characters.
      highlightSignature: {
        schema: {},
      },
      inline: true,
      toolbar: false,
      toolbarInline: true,
      toolbarInlineForSelection: props.plainText ? false : true,
      hideToolbar: false,
      toolbarTimeout: 0,
      placeholder: fieldPlaceholder,
      readonly: props.readonly,
      enableDragAndDropFileToEditor: true,
      saveModeInStorage: true,
      enter: props.plainText ? 'br' : 'div',
      popup: {
        a: null,
        selection: Jodit.atom([
          'paragraph',
          '|',
          'bold',
          'italic',
          'underline',
          '|',
          'font',
          'fontsize',
          'brush',
          'lineHeight',
          '|',
          'ul',
          'ol',
          '|',
          'indent',
          'outdent',
          'left',
          'center',
          'right',
          'justify',
          '|',
          'link',
          'designFileLink',
          'table',
          '|',
          'image',
          'variables',
        ]),
      },
      uploader: {
        ...Jodit.defaultOptions.uploader,
        headers,
        url: Endpoint.dumpUrl('@gc/folder/rte-upload'),
        insertImageAsBase64URI: false,
        imagesExtensions: ['jpg', 'png', 'jpeg', 'gif'],
        format: 'json',
      },

      // `showToolip` (sic) is not a Jodit option, so button tooltips kept the default ~1s delay.
      showTooltip: true,
      showTooltipDelay: 0,
      useNativeTooltip: false,

      toolbarAdaptive: false,
      colorPickerDefaultTab: 'color',
      preset: 'inline',
      askBeforePasteHTML: false,
      defaultActionOnPaste: 'insert_clear_html',
      allowResizeX: true,
      // 'link' kept in the always-visible inline toolbar too — in the selection popup alone it was
      // too easy to miss, and attaching a link to selected text is a common need.
      buttons: Jodit.atom(['bold', 'italic', 'fontsize', 'link', 'designFileLink']),
      controls: {
        // One-click alternative to the "Insert link" popup: links the selected text to the proposal's
        // Design File without anyone typing a URL. The href keeps the {{design_file_url}} token, which
        // is resolved when the proposal is rendered — so templates can link to a file chosen later.
        designFileLink: {
          name: 'Design File',
          // No icon on purpose: two link glyphs side by side were indistinguishable, so this renders
          // as a text label like the neighbouring "Short Codes" button.
          tooltip: 'Link the selected text to this proposal’s Design File',
          exec: (editor: any) => {
            const url = '{{design_file_url}}';
            const hasSelection = !editor.s.isCollapsed?.();
            if (hasSelection) {
              // Wrap the selected text — building an <a> from editor.s.html replaced the selection
              // with the label instead of linking it.
              editor.execCommand('createlink', false, url);
            } else {
              // Nothing selected — drop in a labelled link so the button still does something useful.
              editor.s.insertHTML(`<a href="${url}">View Design File</a>`);
            }
            editor.editor.querySelectorAll(`a[href="${url}"]`).forEach((anchor: HTMLAnchorElement) => {
              anchor.setAttribute('target', '_blank');
              anchor.setAttribute('rel', 'noopener noreferrer');
            });
          },
        },
        variables: {
          name: 'Short Codes',
          list: props.list || {
            'h:Your Company': 'h:Your Company',
            company_name: 'Company Name',
            brand_name: 'Brand Name',
            user_name: 'User Name',
            user_email: 'User Email',
            // user_phone: 'User Phone',
            company_address: 'Address',
            company_address2: 'Address 2',
            company_city: 'City',
            company_state: 'State',
            company_country: 'Country',
            company_zip: 'Zip',
            // '|1': '|',
            // Resolves to the Design File selected for the proposal (empty in a template preview)
            'h:Design File': 'h:Design File',
            design_file_link: 'Design File Link',
            design_file_name: 'Design File Name',
            design_file_url: 'Design File URL',
            'h:Your Client': 'h:Your Client',
            org_name: 'Organization Name',
            contact_first_name: 'Contact First Name',
            contact_last_name: 'Contact Last Name',
            contact_name: 'Contact Name',
            contact_email: 'Contact Email',
            contact_address: 'Address',
            contact_address2: 'Address 2',
            contact_city: 'City',
            contact_state: 'State',
            contact_zip: 'Zip Code',
            contact_country: 'Country',
            contact_website: 'Website',
            contact_phone: 'Phone',
            // '|2': '|',
            'h:Proposal': 'h:Proposal',
            // proposal_name: 'Name',
            date_sumitted: 'Date Submitted',
            date_accepted: 'Date Accepted',
            today_date: 'Today Date',
            start_date: 'Start Date',
            end_date: 'End Date',
            total: 'Total ($)',
            contract_price: 'Contract Price ($)',
            weeks_to_complete: 'Weeks to Complete',
            proposal_title: 'Proposal Title',
            proposal_number: 'Proposal Number',
            ...(extraShortCodes ?? {}),
          },
          childTemplate: (editor, key, value) => {
            if (value.startsWith('h:')) {
              return `<span style="font-weight: bold; text-align: lefer" class="${key}">${editor.i18n(value.substring(2))}</span>`;
            }
            return `<span style="font-size: 12px; text-align: left" class="${key}">${editor.i18n(value)}</span>`;
          },
          exec: function (editor, _, { control }) {
            const value = control.args && control.args.length && control.args[0];
            if (value.startsWith('h:')) return false;
            if (value.startsWith('|')) return false;

            if (value) {
              (editor as Jodit).s.insertHTML(`{{${value}}}`);
            }
          },
        },
      },
      theme: props.theme || 'default',
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      autofocus: autoFocus,
      events: {
        afterInit(instance: Jodit) {
          editorInstance = instance;
          // On Backspace, Jodit deletes a char in its own keydown handler but does NOT preventDefault,
          // so the browser's native contentEditable deletion ALSO runs — removing TWO chars per press.
          // Block only the native backward deletion (Jodit already did it). Forward Delete is left to
          // the browser since Jodit doesn't handle it (blocking it would disable the Delete key).
          instance.editor.addEventListener('beforeinput', (e: any) => {
            if (e.inputType === 'deleteContentBackward') {
              e.preventDefault();
            }
          });

          // Highlight {{tokens}} non-destructively (CSS Custom Highlight API, no DOM mutation).
          attachShortcodeHighlight(instance.editor);

          // Picking black in the text colour picker otherwise does nothing over an inherited colour
          attachBlackForecolor(instance);

          // Track toolbar interactions
          (instance as any)._toolbarInteracting = false;

          // Add event listeners to toolbar to track interactions
          setTimeout(() => {
            const toolbar = instance.toolbarInline?.container || document.querySelector('.jodit-toolbar-inline');
            if (toolbar) {
              toolbar.addEventListener('mousedown', () => {
                (instance as any)._toolbarInteracting = true;
              });
              toolbar.addEventListener('mouseup', () => {
                setTimeout(() => {
                  (instance as any)._toolbarInteracting = false;
                }, 300);
              });
              toolbar.addEventListener('click', () => {
                (instance as any)._toolbarInteracting = true;
                setTimeout(() => {
                  (instance as any)._toolbarInteracting = false;
                }, 500);
              });
            }
          }, 100);

          // Store reference for selection monitoring
          (instance as any)._selectionMonitor = setInterval(() => {
            const selection = instance.s.sel;
            if (selection && !selection.isCollapsed) {
              // Text is selected - show toolbar
              if (instance.toolbarInline && !instance.toolbarInline.isOpened) {
                instance.toolbarInline.show();
              }
            } else {
              // No text selected - hide toolbar (but not if interacting with toolbar)
              if (!(instance as any)._toolbarInteracting) {
                setTimeout(() => {
                  const laterSelection = instance.s.sel;
                  if (!laterSelection || laterSelection.isCollapsed) {
                    if (!(instance as any)._toolbarInteracting && instance.toolbarInline && instance.toolbarInline.isOpened) {
                      instance.toolbarInline.hide();
                    }
                  }
                }, 300);
              }
            }
          }, 100);
        },
        focus() {
          handleFocus();
        },
        click() {
          handleFocus();
        },
        selectionchange(instance: Jodit) {
          // Immediate response to selection changes
          setTimeout(() => {
            const selection = instance.s.sel;
            if (selection && !selection.isCollapsed) {
              // Text is selected - show toolbar
              if (instance.toolbarInline) {
                instance.toolbarInline.show();
              }
            } else {
              // No text selected - hide toolbar (but not if interacting with toolbar)
              if (!(instance as any)._toolbarInteracting) {
                setTimeout(() => {
                  const laterSelection = instance.s.sel;
                  if (!laterSelection || laterSelection.isCollapsed) {
                    if (!(instance as any)._toolbarInteracting && instance.toolbarInline) {
                      instance.toolbarInline.hide();
                    }
                  }
                }, 200);
              }
            }
          }, 10);
        },
        afterCommand() {
          // After a command, just keep the inline toolbar visible.
          // NOTE: this used to "restore selection" by selecting the last element's contents when the
          // caret was collapsed. That hijacked the caret into a full selection, so the next keypress /
          // backspace wiped a whole element (the "delete one char removes several" bug). Removed.
          // Jodit calls this with (command, showUI, value), not the editor: it used to read `false` as the editor and
          // throw after every toolbar command, which also left the colour picker open and skipped Jodit's value sync.
          const instance = editorInstance;
          if (!instance) return;
          (instance as any)._toolbarInteracting = true;

          setTimeout(() => {
            if (instance.toolbarInline) {
              instance.toolbarInline.show();
            }

            setTimeout(() => {
              (instance as any)._toolbarInteracting = false;
            }, 1000);
          }, 100);
        },
        beforeDestruct(instance: Jodit) {
          // Clean up interval
          if ((instance as any)._selectionMonitor) {
            clearInterval((instance as any)._selectionMonitor);
          }
        },
      },
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldPlaceholder, fieldReadonly, props.minHeight, props.list, props.theme, autoFocus, extraShortCodes]);

  const transformedFieldsValue = useMemo(() => {
    if (!props.readonly) return fieldValue;
    return (
      fieldValue
        // The editor's {{token}} highlight (highlightSignature, #FFFF0045 ≈ rgba(255,255,0,0.27)) can get
        // persisted into the saved HTML. Strip it so resolved values render clean (like the other codes).
        .replace(/background-color:\s*(?:rgba\(\s*255\s*,\s*255\s*,\s*0\s*,[^)]*\)|#ffff0045)\s*;?/gi, '')
        .replace(/{{([^}]+)}}/g, (match, p1) => {
          const value = props.transformedFieldsValue && props.transformedFieldsValue[p1];
          if (!value) return '';
          return value || match;
        })
        // A link built from a short code (e.g. the "Design File" button writes
        // href="{{design_file_url}}") has an empty href when that code resolves to nothing — e.g. a
        // proposal with no design file selected. Unwrap it so the text stays put instead of leaving
        // a dead link that reloads the page.
        .replace(/<a\b[^>]*href=(?:""|'')[^>]*>([\s\S]*?)<\/a>/gi, '$1')
    );
  }, [fieldValue, props.readonly, props.transformedFieldsValue]);

  return (
    <div className="jodit-inline" style={{ flex: 1 }}>
      {!!props.readonly && glossarify && GlossaryService.getGlossaryContent(transformedFieldsValue)}
      {!!props.readonly && !glossarify && HTMLService.toReact(transformedFieldsValue)}
      {!props.readonly && (
        <JoditEditor key={joditKey} ref={joditRef} config={config as any} onBlur={handleBlur} onChange={handleChangeText} value={myValue} />
      )}
    </div>
  );
}
